import { useState } from 'react';
import { useNavigate } from 'react-router';

import { paths } from '@/app/router/paths';
import { Button, cn, type ButtonSize } from '@/design-system';
import { describeOrderProblem, type Order, type OrderAccess } from '@/entities/order';
import { useSessionStore } from '@/shared/api';
import { useOnlineStatus, useSyncState, useTenantDb } from '@/shared/offline';
import { TakeOnRolePanel } from '@/shared/ui';

import { surveyActionCopy as copy } from '../copy';
import { useStartOrder } from '../hooks';
import { prepareSurvey } from '../prepareSurvey';
import { problemText } from '../problemText';
import { surveyActionFor, type SurveyAction } from '../surveyAction';

export interface SurveyActionButtonProps {
    action: SurveyAction;
    /** Start de keuring; alleen nodig voor `start` en `takeOver`. */
    onStart?: (takeOver: boolean) => void;
    /** Ga naar het scherm. Standaard navigeert de knop zelf. */
    onOpen?: (to: string) => void;
    busy?: boolean;
    /** Wat de knop zegt terwijl hij bezig is, als dat iets anders is dan starten of overnemen. */
    busyLabel?: string | null;
    /** Waarom de knop nu niet kan, in gewone taal. Amber, nooit rood. */
    blocked?: string | null;
    /** De zin die zegt wie de keuring heeft laten staan, ook als `action` er geen heeft. */
    problem?: string | null;
    size?: ButtonSize;
    fullWidth?: boolean;
    /** Zonder de zin eronder: in een rij van een tabel is daar geen plek voor. */
    quiet?: boolean;
    /** Op de donkere kop van een keuring. */
    onDark?: boolean;
    className?: string;
}

/**
 * De knop die bij een keuring hoort, overal dezelfde: Mijn dag, de lijst Keuringen en
 * de kop van één keuring. Eén `primary`-knop, en daaronder in gewone taal wat er
 * speelt — van wie de keuring is, of waarom starten nu niet gaat.
 *
 * Alles is minstens 44 px hoog (`size="md"`), want dit is de knop waarop iemand met
 * één hand, in een gang, met handschoenen aan drukt.
 */
export function SurveyActionButton({
    action,
    onStart,
    onOpen,
    busy = false,
    busyLabel = null,
    blocked = null,
    problem = null,
    size = 'md',
    fullWidth = false,
    quiet = false,
    onDark = false,
    className,
}: SurveyActionButtonProps) {
    const navigate = useNavigate();
    const note = action.kind === 'takeOver' || action.kind === 'note' ? action.note : null;

    function open(to: string) {
        if (onOpen) {
            onOpen(to);

            return;
        }

        void navigate(to);
    }

    return (
        <div className={cn('flex min-w-0 flex-col gap-[6px]', fullWidth && 'w-full', className)}>
            {action.kind === 'open' ? (
                <Button
                    variant="primary"
                    size={size}
                    fullWidth={fullWidth}
                    disabled={busy}
                    onClick={(event) => {
                        /* In een rij van de lijst zou de klik anders óók de rij selecteren. */
                        event.stopPropagation();
                        open(action.to);
                    }}
                >
                    {action.label}
                </Button>
            ) : action.kind === 'start' || action.kind === 'takeOver' ? (
                <Button
                    variant={action.kind === 'start' ? 'primary' : 'outline'}
                    size={size}
                    fullWidth={fullWidth}
                    disabled={busy || blocked !== null}
                    onClick={(event) => {
                        event.stopPropagation();
                        onStart?.(action.kind === 'takeOver');
                    }}
                >
                    {busy
                        ? (busyLabel ??
                          (action.kind === 'takeOver' ? copy.takingOver : copy.starting))
                        : action.label}
                </Button>
            ) : null}

            {note && !quiet ? (
                <Note tone="quiet" onDark={onDark}>
                    {note}
                </Note>
            ) : null}
            {blocked ? (
                <Note tone="warn" onDark={onDark}>
                    {blocked}
                </Note>
            ) : null}
            {problem ? (
                <Note tone="warn" onDark={onDark}>
                    {problem}
                </Note>
            ) : null}
        </div>
    );
}

export interface OrderSurveyActionProps {
    order: Order;
    access: OrderAccess;
    size?: ButtonSize;
    fullWidth?: boolean;
    quiet?: boolean;
    onDark?: boolean;
    className?: string;
}

/**
 * Diezelfde knop, voor een keuring uit de API: hij weet zelf wat er te doen is, doet
 * het, en loopt daarna de opname in.
 *
 * Drie dingen doet hij eromheen:
 *
 * - **Starten vraagt verbinding.** De keuring wordt op de server toegewezen, gepland en
 *   doorgezet; dat kan niet uit de outbox, want de opname moet er zijn vóór je hem
 *   invult. Offline staat het er in amber bij en blijft "Opname hervatten" gewoon werken.
 * - **Na het starten haalt hij de opname op.** Het opnamescherm leest de lokale opslag,
 *   dus zonder een ronde synchronisatie zou je op "staat nog niet op dit apparaat"
 *   landen. Lukt die ronde niet, dan gaan we toch: het scherm legt zelf uit wat er mist.
 * - **De eigenaar die zelf keurt** krijgt eerst de vraag of hij ook als inspecteur wil
 *   werken. Hij mág opnemen, maar zonder die rol staat de keuring niet in zijn Mijn dag,
 *   en dan is hij hem morgen kwijt.
 */
export function OrderSurveyAction({
    order,
    access,
    size = 'md',
    fullWidth = false,
    quiet = false,
    onDark = false,
    className,
}: OrderSurveyActionProps) {
    const navigate = useNavigate();
    const online = useOnlineStatus();
    const { flushNow } = useSyncState();
    const start = useStartOrder();
    const db = useTenantDb();
    const roles = useSessionStore((state) => state.session?.roles ?? []);
    const [asking, setAsking] = useState<boolean | null>(null);
    const [preparing, setPreparing] = useState(false);

    const action = surveyActionFor(order, access);

    async function run(takeOver: boolean) {
        const started = await start.mutateAsync({ orderUuid: order.uuid, takeOver });

        /*
         * Het opnamescherm leest alleen dit apparaat. Dus eerst de opname en haar vragenlijst
         * ophalen, en pas dan erheen; duurt dat te lang, dan toch — het scherm zegt dan dat hij
         * wordt klaargezet, en opent vanzelf als hij binnenkomt. Zonder lokale opslag (meekijken)
         * valt er niets op te halen: het scherm legt dat zelf uit.
         */
        if (db) {
            setPreparing(true);
            await prepareSurvey({ db, inspectionUuid: started.inspectionUuid, sync: flushNow });
        }

        void navigate(
            started.order.inspection
                ? paths.inspectionFor(started.order.inspection)
                : paths.survey(started.inspectionUuid),
        );
    }

    function begin(takeOver: boolean) {
        if (!access.field) {
            setAsking(takeOver);

            return;
        }

        void run(takeOver).catch(() => undefined);
    }

    const blocked = !online && (action.kind === 'start' || action.kind === 'takeOver');

    return (
        <>
            <SurveyActionButton
                action={action}
                size={size}
                fullWidth={fullWidth}
                quiet={quiet}
                onDark={onDark}
                className={className}
                busy={start.isPending || preparing}
                busyLabel={preparing ? copy.preparing : null}
                blocked={blocked ? copy.offline : null}
                problem={start.error ? refusalText(start.error) : null}
                onStart={begin}
            />

            <TakeOnRolePanel
                open={asking !== null}
                roles={roles}
                onClose={() => {
                    const takeOver = asking;

                    setAsking(null);

                    /* De vraag is gesteld; ja of nee, starten is waar je op drukte. */
                    if (takeOver !== null) {
                        void run(takeOver).catch(() => undefined);
                    }
                }}
            />
        </>
    );
}

/**
 * Wat er misging, in gewone taal.
 *
 * Een keuring die niet te starten is, heeft van de API al een hele zin gekregen —
 * "Mark Dekker is al met de opname bezig." — en die is beter dan wat wij eromheen
 * kunnen zetten. Al het andere krijgt wél het voorzetsel, anders staat er een losse
 * foutmelding zonder te zeggen waar hij over gaat.
 */
function refusalText(error: unknown): string {
    const problem = describeOrderProblem(error);

    return problem.kind === 'notStartable' ? problem.message : copy.failed(problemText(problem));
}

/**
 * De regel onder de knop. Amber, nooit rood: er is niets stuk, er kan alleen even
 * iets niet — de huisregel voor offline en voor werk van een collega.
 */
function Note({
    tone,
    onDark,
    children,
}: {
    tone: 'quiet' | 'warn';
    onDark: boolean;
    children: string;
}) {
    return (
        <p
            role={tone === 'warn' ? 'status' : undefined}
            className={cn(
                'm-0 text-[12px] leading-[1.4]',
                tone === 'warn'
                    ? onDark
                        ? 'text-[var(--warn-field)]'
                        : 'text-[var(--warn-text)]'
                    : onDark
                      ? 'text-[var(--text-on-dark-4-safe)]'
                      : 'text-[var(--text-tertiary-safe)]',
            )}
        >
            {children}
        </p>
    );
}
